// cSpell:ignore Afrekeningen Bedrag Gebouw GEBOUW Naam Periode Privaat PRIVAAT
// The Afrekeningen list, in the cost list's style (Lewis, 2026-10-16): the
// same header, rows, cells and grid as the cost list above it, so the two
// tables read as one. Its columns and texts: settlementListColumns.utils.
//   Naam | Datum | Type | Periode | Status | Bedrag | menu
// in the cost list's order, with Type only inside a building and the
// property in its place on the /costs overview. Above it the cost list's
// filters: a search, the type, the year and the status (Lewis, 2026-10-21;
// settlementListFilters.utils).

import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import {
  DropdownMenu,
  EmptyState,
  Filters,
  Icon,
  Lozenge,
  Pagination,
  Select,
  Tooltip,
} from "@rentiohq/web-shared/dist/components";
import React from "react";
import styled from "styled-components";
import { periodRangeLines } from "../costPeriod.utils";
import {
  ESettlementListColumn,
  ISettlementListPage,
  ISettlementLozenge,
  ISettlementRowLevel,
  PAY_STATUS_SEPARATOR,
  settlementListColumnAlign,
  settlementListColumnHeader,
  settlementListColumns,
  settlementListColumnSpan,
  settlementListGridTemplate,
  TSettlementPayStatus,
} from "../settlementListColumns.utils";
import {
  ESettlementStatusFilter,
  ESettlementTypeFilter,
  ISettlementFilterRow,
  settlementMatchesFilters,
  settlementYears,
} from "../settlementListFilters.utils";
import {
  ListAligned,
  ListAmount,
  ListCell,
  ListCellText,
  ListFilterRow,
  ListFrame,
  ListHeaderCell,
  ListHeaderRow,
  ListLozengeBox,
  ListMenuButton,
  ListOneLine,
  ListRow,
  ListSubLine,
  ListSubText,
  ListTitle,
  ListToolBar,
} from "./listTable.styles";
import { ListTypeLozenge } from "./ListTypeLozenge";
import { TypeColumnHeader } from "./TypeColumnHeader";

export interface SettlementRowVM {
  id: string;
  // The settlement's kind (settlementRowTexts); under it a departure's
  // tenants and the description.
  name: string;
  nameSubLines: string[];
  confirmedOn: string;
  period: string;
  // For the filters (settlementRowTexts).
  typeKey: ISettlementFilterRow["typeKey"];
  periodFrom: string;
  periodTo: string;
  // Inside a building: "Gebouw" or "Privaat" (settlementRowLevel); none for
  // a departure.
  level?: ISettlementRowLevel;
  // Across properties: the settlement's property.
  propertyLabel?: string;
  // How far its tenants are (settlementPayStatusOf).
  payStatus: TSettlementPayStatus;
  // The total of its costs (settlementAmountTexts).
  amount: { amount: string; subLines: string[] };
  onOpen: () => void;
  // Left out for a user who may only read.
  onDelete?: () => void;
}

// The columns' widths: see costListColumns.utils. Below a 1024 px wide
// screen the table scrolls sideways, as the cost list does.
const Wrap = styled(ListFrame)`
  overflow-x: auto;
`;

// Same page size as the costs table above it, so both lists paginate in step.
const PAGE_SIZE = 20;

export const SettlementsTable: React.FC<{
  rows: SettlementRowVM[];
  // Inside a building: the Type column. Across properties: the property.
  page: ISettlementListPage;
}> = ({ rows, page: listPage }) => {
  const [page, setPage] = React.useState(0);
  const [query, setQuery] = React.useState("");
  const [typeFilter, setTypeFilter] = React.useState(ESettlementTypeFilter.All);
  const [yearFilter, setYearFilter] = React.useState("");
  const [statusFilter, setStatusFilter] = React.useState(
    ESettlementStatusFilter.All,
  );

  const filterRowOf = (row: SettlementRowVM): ISettlementFilterRow => ({
    ...row,
    unitAddress: row.level?.unitAddress,
    progress: row.payStatus.progress,
  });
  const yearOptions = React.useMemo(
    () => settlementYears(rows.map(filterRowOf)),
    [rows],
  );
  const filtered = React.useMemo(
    () =>
      rows.filter(row =>
        settlementMatchesFilters({
          row: filterRowOf(row),
          query,
          type: typeFilter,
          year: yearFilter,
          status: statusFilter,
        }),
      ),
    [rows, query, typeFilter, yearFilter, statusFilter],
  );
  React.useEffect(() => {
    setPage(0);
  }, [query, typeFilter, yearFilter, statusFilter]);
  const handleClearFilters = () => {
    setQuery("");
    setTypeFilter(ESettlementTypeFilter.All);
    setYearFilter("");
    setStatusFilter(ESettlementStatusFilter.All);
  };

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  // Deleting rows can strand the current page past the end — clamp back.
  React.useEffect(() => {
    if (page > 0 && page >= totalPages) setPage(Math.max(0, totalPages - 1));
  }, [page, totalPages]);
  const pageRows = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const columns = settlementListColumns(listPage);
  const gridColumns = settlementListGridTemplate(listPage);

  // The title as the cost list writes a cost's, its lines under it.
  const renderName = (row: SettlementRowVM) => (
    <ListCell title={[row.name, ...row.nameSubLines].join("\n")}>
      <ListTitle>{row.name}</ListTitle>
      {row.nameSubLines.map(line => (
        <ListSubLine key={line}>{line}</ListSubLine>
      ))}
    </ListCell>
  );

  // "GEBOUW" or "PRIVAAT" as the cost list writes them, a unit's address in
  // the tooltip of PRIVAAT; nothing for a departure.
  const renderLevel = (row: SettlementRowVM) =>
    row.level ? (
      <ListTypeLozenge
        label={row.level.label}
        appearance={row.level.appearance}
        address={row.level.unitAddress}
      />
    ) : null;

  // As the cost list's Status: a plain lozenge, the whole text as tooltip
  // when the column cuts it; under it what is still open (wrapping between
  // its parts on a narrow screen, never cut) or the day it was all done.
  const renderLozenge = ({ label, appearance, sub }: ISettlementLozenge) => (
    <ListLozengeBox>
      <Tooltip
        tooltipContent={[label, sub].filter(Boolean).join(PAY_STATUS_SEPARATOR)}
        noCursor
      >
        <span className="lozenge">
          <Lozenge appearance={appearance}>{label}</Lozenge>
        </span>
      </Tooltip>
      {sub && (
        <ListSubText>
          {/* Each part on one line; a narrow column breaks between them. */}
          {sub.split(PAY_STATUS_SEPARATOR).map((part, index) => (
            <React.Fragment key={part}>
              {index > 0 && PAY_STATUS_SEPARATOR}
              <span className="part">{part}</span>
            </React.Fragment>
          ))}
        </ListSubText>
      )}
    </ListLozengeBox>
  );

  // A draft has no payments yet: a dash, as the cost list writes nothing.
  const renderPayStatus = ({ payStatus }: SettlementRowVM) =>
    renderLozenge(payStatus);

  const renderCellContent = (
    row: SettlementRowVM,
    column: ESettlementListColumn,
  ) => {
    switch (column) {
      case ESettlementListColumn.Name:
        return renderName(row);
      // The date and the period in the cost list's own cell, the period
      // breaking after its arrow only.
      case ESettlementListColumn.ConfirmedOn:
        return <ListCellText>{row.confirmedOn}</ListCellText>;
      case ESettlementListColumn.Period:
        return (
          <ListCellText>
            {periodRangeLines(row.period).map((line, index) => (
              <React.Fragment key={line}>
                {index > 0 && " "}
                <span className="nowrap">{line}</span>
              </React.Fragment>
            ))}
          </ListCellText>
        );
      case ESettlementListColumn.Level:
        return renderLevel(row);
      case ESettlementListColumn.Property:
        return (
          <ListCell>
            <ListOneLine title={row.propertyLabel}>
              {row.propertyLabel || "—"}
            </ListOneLine>
          </ListCell>
        );
      case ESettlementListColumn.PayStatus:
        return renderPayStatus(row);
      // As the cost list writes a cost's amount, "+ € x btw" under it.
      case ESettlementListColumn.Amount:
        return (
          <ListAmount>
            <div className="amount">{row.amount.amount}</div>
            {row.amount.subLines.map(line => (
              <div key={line} className="sub" title={line}>
                {line}
              </div>
            ))}
          </ListAmount>
        );
    }
  };

  const renderRow = (row: SettlementRowVM) => (
    <ListRow key={row.id} $columns={gridColumns} onClick={row.onOpen}>
      {columns.map(column => (
        <ListAligned
          key={column}
          $align={settlementListColumnAlign(column)}
          $span={settlementListColumnSpan({ column, page: listPage })}
        >
          {renderCellContent(row, column)}
        </ListAligned>
      ))}
      {/* DropdownMenu overrides its child's onClick with its own toggle, so
      the stopPropagation must live on a wrapper — otherwise the row's
      onOpen fires and the sidebar opens on a menu click. */}
      <div onClick={e => e.stopPropagation()}>
        <DropdownMenu
          actions={[
            {
              content: getLocalizedText("costs.action.open"),
              onClick: row.onOpen,
            },
            ...(row.onDelete
              ? [
                  {
                    content: getLocalizedText("costs.action.delete"),
                    onClick: row.onDelete,
                  },
                ]
              : []),
          ]}
        >
          <ListMenuButton
            type="button"
            aria-label={getLocalizedText("costs.action.more")}
          >
            <Icon source="navigationMenuVertical" />
          </ListMenuButton>
        </DropdownMenu>
      </div>
    </ListRow>
  );

  const selectOption = (opt: { value?: string } | null) => opt?.value ?? "";

  // The cost list's filters, in the same row and widths.
  const renderToolbar = () => (
    <ListToolBar>
      <ListFilterRow>
        <div className="search">
          <Filters
            queryValue={query}
            queryPlaceholder={getLocalizedText(
              "settlement.list.search_placeholder",
            )}
            onQueryChange={setQuery}
            onQueryClear={() => setQuery("")}
          />
        </div>
        <div className="select">
          <Select
            id="settlement-filter-type"
            label="_"
            labelHidden
            value={typeFilter}
            options={[
              {
                value: ESettlementTypeFilter.All,
                label: getLocalizedText("settlement.list.filter.all_types"),
              },
              {
                value: ESettlementTypeFilter.Building,
                label: getLocalizedText("costs.label.building"),
              },
              {
                value: ESettlementTypeFilter.Unit,
                label: getLocalizedText("costs.label.private"),
              },
              {
                value: ESettlementTypeFilter.Departure,
                label: getLocalizedText("settlement.title.departure_short"),
              },
            ]}
            onChange={(opt: { value?: string } | null) =>
              setTypeFilter(selectOption(opt) as ESettlementTypeFilter)
            }
          />
        </div>
        <div className="select">
          <Select
            id="settlement-filter-year"
            label="_"
            labelHidden
            value={yearFilter}
            options={[
              {
                value: "",
                label: getLocalizedText("costs.list.filter.all_years"),
              },
              ...yearOptions.map(year => ({
                value: String(year),
                label: getLocalizedText("costs.list.filter.period_year", {
                  year: String(year),
                }),
              })),
            ]}
            onChange={(opt: { value?: string } | null) =>
              setYearFilter(selectOption(opt))
            }
          />
        </div>
        <div className="select">
          <Select
            id="settlement-filter-status"
            label="_"
            labelHidden
            value={statusFilter}
            // Worded like the statuses the rows show.
            options={[
              {
                value: ESettlementStatusFilter.All,
                label: getLocalizedText("costs.list.filter.all_statuses"),
              },
              {
                value: ESettlementStatusFilter.Open,
                label: getLocalizedText("settlement.list.filter.open"),
              },
              {
                value: ESettlementStatusFilter.Done,
                label: getLocalizedText("costs.settlement.status.completed"),
              },
              {
                value: ESettlementStatusFilter.Nothing,
                label: getLocalizedText(
                  "costs.settlement.status.nothing_to_pay",
                ),
              },
            ]}
            onChange={(opt: { value?: string } | null) =>
              setStatusFilter(selectOption(opt) as ESettlementStatusFilter)
            }
          />
        </div>
      </ListFilterRow>
    </ListToolBar>
  );

  if (filtered.length === 0 && rows.length > 0) {
    return (
      <>
        {renderToolbar()}
        <EmptyState
          heading={getLocalizedText("search.no_results")}
          icon="billSplit"
          action={{
            content: getLocalizedText("application.filters.panel.clear"),
            onClick: handleClearFilters,
            appearance: "outline",
          }}
        />
      </>
    );
  }

  return (
    <>
      {renderToolbar()}
      <Wrap>
        {/* The cost list's header, on the cost list's grid. */}
        <ListHeaderRow $columns={gridColumns} $spread>
          {columns.map(column => (
            <ListHeaderCell
              key={column}
              $align={settlementListColumnAlign(column)}
              $span={settlementListColumnSpan({ column, page: listPage })}
            >
              {column === ESettlementListColumn.Level ? (
                <TypeColumnHeader kind="settlement" />
              ) : (
                settlementListColumnHeader(column)
              )}
            </ListHeaderCell>
          ))}
          <ListHeaderCell />
        </ListHeaderRow>
        {pageRows.map(renderRow)}
      </Wrap>
      {/* Outside the table's frame, which reaches beyond the card's content. */}
      {totalPages > 1 && (
        <Pagination
          pageCount={totalPages}
          initialPage={page}
          onPageChange={({ selected }: { selected: number }) =>
            setPage(selected)
          }
        />
      )}
    </>
  );
};
